import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { getBackend } from '../backend';
import { Row, SetupListPanel, SetupDrill, confirmDiscardIfDirty, useReportDirty, formsEqual, orgSubLabel, isMasterRole, useAutoSelectFirstInLandscape, usePersistedSelection, useSaveSelection, setupPersistedSelection, setupPersistedSubTab, saveSetupSubTab } from './setupCommon';
import { askConfirm } from './confirmService';
import { RightsChecklist, DEFAULT_RIGHTS, IMPLICIT_RIGHTS, rightsSummary } from './RightsChecklist';
import { AccessTree } from './AccessTree';
import { hasCamera, toggleCamera, maskForCameras, hasAllCameras, cameraCount, countOf } from './cameraBits';

/**
 * CloudUserSetup — cloud user administration (rights-acl design §8; per-device grants + user disable).
 *
 * The model is per-device (rights-acl §3): a user has RIGHTS and per-camera ACCESS scoped per device
 * (= NVR). Customer/site are device descriptors, not access scopes. The IA (David 2026-07-24) keeps the
 * chrome shallow — no drill-into-a-sub-panel:
 *   - Users list: [Active | Disabled] tabs. Each card shows name / email / a compact SITE access line,
 *     an end-of-row Disable/Enable (confirm-guarded), and taps to EXPAND a read-only access peek (the
 *     common "what can this user reach" check) with an Edit button. The filter also matches access, so
 *     typing a site name narrows to who can reach it.
 *   - Detail (via Edit): General (identity + master bit) and Access tabs.
 *   - Access is an ACCORDION: each granted device is a row; tap to open its rights + camera grid inline
 *     (no separate panel). "+ Add devices" opens the customer▸site▸device finder. A collapsed quick-set
 *     applies rights/cameras across all granted devices.
 *
 * The BACKEND (/ve/user, /ve/user/disable) is the authoritative gate; this UI only mirrors it. ONE
 * getUsers() call feeds the whole screen. Shared pieces: RightsChecklist + AccessTree + cameraBits.
 */

// A user's grant array, normalized: stable types (bRights number, bCameras decimal string) and sorted
// by sDeviceID so `formsEqual` treats a re-toggled-back grant set as pristine (no false dirty). Every
// grant always carries the IMPLICIT rights (ACCESS + REMOTE) — cloud is always remote, so REMOTE is
// on-and-hidden on every device (matches RightsChecklist hiding it).
const sortGrants = (arr) => (arr || [])
  .map((g) => ({ sDeviceID: g.sDeviceID, bRights: (Number(g.bRights) || 0) | IMPLICIT_RIGHTS, bCameras: String(g.bCameras == null ? '0' : g.bCameras) }))
  .sort((a, b) => (a.sDeviceID < b.sDeviceID ? -1 : a.sDeviceID > b.sDeviceID ? 1 : 0));

const blankUser = () => ({
  id: null, email: '', description: '',
  role: 0, grants: [], password: '', generatePassword: true,
});

const userToForm = (u) => ({
  id: u.bID,
  email: u.sName || '',
  description: u.sDescription || '',
  role: Number(u.bRole) || 0,        // 0/1 master bit (rights-acl §2) — everyone else is 0
  grants: sortGrants(u.grants),      // authoritative per-device grants
  password: '', generatePassword: false,
});

const isMasterUser = (u) => Number(u.bRole) === 1;

// Small outline trash icon (strokes currentColor so it themes) — the remove-device affordance.
const TrashIcon = () => (
  <svg className="setup-trash" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

export const CloudUserSetup = (props) => {
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [selectedId, setSelectedId] = useState(null);   // bID, 'new', or null
  const [form, setForm] = useState(null);
  const [pristine, setPristine] = useState(null);       // saved/loaded snapshot; dirty = form differs
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState('');
  const [listTab, setListTab] = useState('active');          // 'active' | 'disabled'
  const [activeTab, setActiveTabRaw] = useState('general');     // detail tab: 'general' | 'access'
  const setActiveTab = useCallback((id) => { setActiveTabRaw(id); saveSetupSubTab('users', id); }, []);
  const [accessNav, setAccessNav] = useState({ mode: 'list' }); // 'list' | 'add' (the finder)
  const [expandedGrantId, setExpandedGrantId] = useState(null); // Access-tab device editor (accordion)
  const [showQuickSet, setShowQuickSet] = useState(false);
  const [quickRights, setQuickRights] = useState(DEFAULT_RIGHTS); // Access quick-set state (not the form)
  const [quickAllCams, setQuickAllCams] = useState(true);

  const isMaster = isMasterRole(props.bRole);   // only a Dividia master may see or grant the master bit (rights-acl §2)
  const visibleUsers = useMemo(
    () => (isMaster ? users : users.filter((u) => !isMasterUser(u))),
    [isMaster, users]
  );
  const callerId = props.callerId;     // the logged-in user's own bID — used to block self-disable/delete

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { users: u, customers: c, devices: d, cameras: cam } = await getBackend().getUsers({ token: props.sSess });
      setUsers(u); setCustomers(c); setDevices(d); setCameras(cam);
      return u;
    } catch (e) {
      setError('Could not load users. ' + (e && e.message ? e.message : ''));
      return null;
    } finally {
      setLoading(false);
    }
  }, [props.sSess]);

  useEffect(() => { load(); }, [load]);

  // device id -> device record (for grant summaries + camera lists)
  const devicesById = useMemo(() => {
    const m = {};
    (devices || []).forEach((d) => { if (d && d.sDeviceID) m[d.sDeviceID] = d; });
    return m;
  }, [devices]);

  // device id -> its cameras (deduped by bCamera — cameras are per-profile), sorted by camera number.
  const camsByDevice = useMemo(() => {
    const tmp = {};
    (cameras || []).forEach((c) => {
      if (!c || !c.sDeviceID || c.bCamera == null) return;
      if (!tmp[c.sDeviceID]) tmp[c.sDeviceID] = new Map();
      if (!tmp[c.sDeviceID].has(c.bCamera)) tmp[c.sDeviceID].set(c.bCamera, c);
    });
    const out = {};
    Object.keys(tmp).forEach((k) => { out[k] = [...tmp[k].values()].sort((a, b) => a.bCamera - b.bCamera); });
    return out;
  }, [cameras]);

  const dirty = form !== null && !formsEqual(form, pristine);
  const seed = (f) => { setForm(f); setPristine(f); setError(''); setNotice(''); };
  useReportDirty(dirty);
  useSaveSelection('users', selectedId);
  usePersistedSelection('users', visibleUsers, (u) => u.bID, (u) => {
    const f = userToForm(u);
    setSelectedId(u.bID); seed(f);
    const restoredSubTab = setupPersistedSubTab('users');
    setActiveTabRaw(restoredSubTab === 'access' ? 'access' : 'general');
    setAccessNav({ mode: 'list' }); setShowQuickSet(false);
    setExpandedGrantId(f.grants.length === 1 ? f.grants[0].sDeviceID : null);
  });

  const patch = (p) => { setForm((f) => ({ ...f, ...p })); setNotice(''); };
  const revert = () => { setForm(pristine); setError(''); setNotice(''); };

  const selectUser = async (u) => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    const f = userToForm(u);
    setSelectedId(u.bID); seed(f);
    setActiveTab('general'); setAccessNav({ mode: 'list' }); setShowQuickSet(false);
    // a single-device user: auto-open that device's editor (nothing to choose between, so save the tap)
    setExpandedGrantId(f.grants.length === 1 ? f.grants[0].sDeviceID : null);
  };
  const newUser = async () => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    setSelectedId('new'); seed(blankUser());
    setActiveTab('general'); setAccessNav({ mode: 'list' }); setExpandedGrantId(null); setShowQuickSet(false);
  };
  const closeDetail = async () => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    setSelectedId(null); seed(null); setAccessNav({ mode: 'list' });
  };

  // ---- grant editing (all mutate form.grants, kept sorted/typed for clean dirty tracking) ----
  const camsFor = (sDeviceID) => (camsByDevice[sDeviceID] || []).map((c) => c.bCamera);
  const setGrants = (next) => patch({ grants: sortGrants(next) });
  const patchGrant = (sDeviceID, changes) => setGrants(form.grants.map((g) => (g.sDeviceID === sDeviceID ? { ...g, ...changes } : g)));
  const addGrant = (sDeviceID) => {
    if (form.grants.some((g) => g.sDeviceID === sDeviceID)) return;
    setGrants([...form.grants, { sDeviceID, bRights: DEFAULT_RIGHTS, bCameras: maskForCameras(camsFor(sDeviceID)) }]);
  };
  const removeGrant = (sDeviceID) => setGrants(form.grants.filter((g) => g.sDeviceID !== sDeviceID));
  const toggleGrant = (sDeviceID) => (form.grants.some((g) => g.sDeviceID === sDeviceID) ? removeGrant(sDeviceID) : addGrant(sDeviceID));
  // Remove a device from this user's access, from the (collapsed) device row — confirmed, so a mis-tap
  // can't silently drop access. In-memory like the other grant edits; persists on Save.
  const removeDevice = async (sDeviceID) => {
    if (!(await askConfirm({
      title: 'Remove device access?',
      message: `Remove "${grantDevName(sDeviceID)}" from this user's access? They will no longer see this device or its cameras (applied when you Save).`,
      confirmLabel: 'Remove',
      danger: true,
    }))) return;
    removeGrant(sDeviceID);
    if (expandedGrantId === sDeviceID) setExpandedGrantId(null);
  };
  const applyQuickSet = () => {
    const rights = quickRights | IMPLICIT_RIGHTS;   // ACCESS + REMOTE are always implied on a cloud device
    setGrants(form.grants.map((g) => ({
      ...g,
      bRights: rights,
      bCameras: quickAllCams ? maskForCameras(camsFor(g.sDeviceID)) : g.bCameras,
    })));
    setNotice('Applied to all ' + form.grants.length + (form.grants.length === 1 ? ' device.' : ' devices.'));
  };

  // A non-master may only create a user inside their own ADMIN scope, so cloudapi rejects (400) a new
  // user with NO device grant. Block Create until one is granted (review round 9); masters unchanged.
  const needsGrant = !!form && !form.id && !isMaster && form.grants.length === 0;
  const canSave = !!form && dirty && !!form.email.trim() && (!!form.id || !!form.description.trim()) && !needsGrant;

  const save = async () => {
    if (!form.email.trim()) { setError('Name (email) is required.'); return; }
    if (!form.id && !form.description.trim()) { setError('A display name is required for a new user.'); return; }
    setBusy(true); setError(''); setNotice('');
    try {
      const { ok } = await getBackend().saveUser({
        id: form.id,
        email: form.email.trim(),
        description: form.description,
        role: form.role,          // 0/1 master bit
        grants: form.grants,      // authoritative per-device grants
        password: form.password,
        generatePassword: form.generatePassword,
      }, { token: props.sSess });
      if (!ok) throw new Error('Save was rejected.');
      setNotice(form.id ? 'User saved.' : 'User created — credentials emailed.');
      // Stay on the saved record (re-seeded clean) instead of deselecting to a blank pane. Find it by id
      // (edit) or by email (a fresh create, whose new id the save response doesn't return). (David 2026-08-30)
      const fresh = await load();
      const saved = (fresh || []).find((u) => (form.id ? u.bID === form.id : u.sName === form.email.trim()));
      if (saved) {
        const f = userToForm(saved);
        setSelectedId(saved.bID); seed(f);
        setActiveTab('general'); setAccessNav({ mode: 'list' }); setShowQuickSet(false);
        setExpandedGrantId(f.grants.length === 1 ? f.grants[0].sDeviceID : null);
      } else { setSelectedId(null); setForm(null); setPristine(null); }
    } catch (e) {
      setError('Could not save. ' + (e && e.message ? e.message : 'You may not have permission.'));
    } finally { setBusy(false); }
  };

  const resetPw = async () => {
    if (!form || !form.id) return;
    if (!(await askConfirm({
      title: 'Reset password?',
      message: `Reset the password for "${form.email}"? A new password will be emailed to them.`,
      confirmLabel: 'Reset Password',
    }))) return;
    setBusy(true); setError('');
    try {
      const { ok } = await getBackend().resetUserPassword(form.id, { token: props.sSess });
      if (!ok) throw new Error('Reset was rejected.');
      setNotice('A new password has been emailed to the user.');
    } catch (e) {
      setError('Could not reset password. ' + (e && e.message ? e.message : ''));
    } finally { setBusy(false); }
  };

  // ---- list-row actions (operate on a user, not the open form) ----
  const doSetDisabled = async (u, fDisabled) => {
    if (fDisabled && !(await askConfirm({
      title: 'Disable user?',
      message: `Disable "${u.sName}"? They won't be able to log in until re-enabled.`,
      confirmLabel: 'Disable',
      danger: true,
    }))) return;
    setBusy(true); setError('');
    try {
      const { ok } = await getBackend().setUserDisabled(u.bID, fDisabled, { token: props.sSess });
      if (!ok) throw new Error('Request was rejected.');
      await load();
    } catch (e) {
      setError('Could not update user. ' + (e && e.message ? e.message : ''));
    } finally { setBusy(false); }
  };
  const doDelete = async (u) => {
    if (!(await askConfirm({
      title: 'Delete user permanently?',
      message: `Permanently delete "${u.sName}"? This cannot be undone.`,
      confirmLabel: 'Delete permanently',
      danger: true,
    }))) return;
    setBusy(true); setError('');
    try {
      const { ok } = await getBackend().deleteUser(u.bID, { token: props.sSess });
      if (!ok) throw new Error('Delete was rejected.');
      if (selectedId === u.bID) { setSelectedId(null); setForm(null); setPristine(null); }
      await load();
    } catch (e) {
      setError('Could not delete. ' + (e && e.message ? e.message : ''));
    } finally { setBusy(false); }
  };

  // ---- grant summary helpers (used by both the list peek and the Access editor) ----
  const grantDevName = (sDeviceID) => { const d = devicesById[sDeviceID]; return (d && d.sName && d.sName.trim()) || sDeviceID; };
  const grantOrg = (sDeviceID) => { const d = devicesById[sDeviceID]; return d ? orgSubLabel(d.sCustomerName, d.sProjectName) : ''; };
  const camSummary = (g) => {
    const cams = camsFor(g.sDeviceID);
    if (cams.length) {
      const n = countOf(g.bCameras, cams);
      if (n === 0) return 'No cameras';
      if (n === cams.length) return `All cameras (${cams.length})`;
      return `${n} of ${cams.length} cameras`;
    }
    const cnt = cameraCount(g.bCameras);
    return cnt === 0 ? 'No cameras' : (cnt === 1 ? '1 camera' : `${cnt} cameras`);
  };

  // The compact, always-on SITE access line on a list card (customer would read the same for everyone in
  // a single-customer deployment, so sites are the meaningful signal). Master / no-access are special.
  const accessLine = (u) => {
    if (isMasterUser(u)) return 'All customers & devices';
    const grants = u.grants || [];
    if (!grants.length) return 'No device access';
    const seen = new Set();
    const sites = [];
    let totalCams = 0;
    grants.forEach((g) => {
      const d = devicesById[g.sDeviceID];
      const site = d && d.sProjectName && d.sProjectName.trim();
      if (site && !seen.has(site)) { seen.add(site); sites.push(site); }
      const cams = camsFor(g.sDeviceID);
      totalCams += cams.length ? countOf(g.bCameras, cams) : cameraCount(g.bCameras);
    });
    const devWord = grants.length === 1 ? '1 device' : grants.length + ' devices';
    const camWord = totalCams === 1 ? '1 camera' : totalCams + ' cameras';
    const sitePart = sites.length
      ? sites.slice(0, 2).join(', ') + (sites.length > 2 ? ` +${sites.length - 2}` : '')
      : '';
    return [sitePart, devWord, camWord].filter(Boolean).join(' · ');
  };

  // Free-text blob of a user's granted device/site/customer names, so the filter can match on ACCESS
  // (type a site name → who can reach it). Master matches "all".
  const userAccessText = (u) => {
    if (isMasterUser(u)) return 'all customers devices';
    return (u.grants || []).map((g) => {
      const d = devicesById[g.sDeviceID];
      return d ? [d.sName, d.sProjectName, d.sCustomerName].filter(Boolean).join(' ') : g.sDeviceID;
    }).join(' ').toLowerCase();
  };

  // ---- derived list state ----
  const isDisabled = (u) => Number(u.fDisabled) === 1;
  // A non-master must never see a Dividia master user (security — rights-acl §2). Filter masters out of
  // the list AND the counts; the backend enforces the same on the /ve/users response (this client filter
  // is defense-in-depth — the backend is the real gate). #86bbm6j8w
  const activeCount = visibleUsers.filter((u) => !isDisabled(u)).length;
  const disabledCount = visibleUsers.filter(isDisabled).length;
  const filtered = visibleUsers.filter((u) => {
    if (listTab === 'active' && isDisabled(u)) return false;
    if (listTab === 'disabled' && !isDisabled(u)) return false;
    const f = filter.trim().toLowerCase();
    if (!f) return true;
    return (u.sName || '').toLowerCase().includes(f)
      || (u.sDescription || '').toLowerCase().includes(f)
      || userAccessText(u).includes(f);
  });
  // mobile-landscape split shows list + detail together → auto-select the first user on first load
  // so the detail pane isn't empty (landscape-only; portrait keeps the list-first drill).
  useAutoSelectFirstInLandscape(filtered, selectedId !== null || setupPersistedSelection('users') != null, selectUser);

  const detailOpen = form !== null;
  const savedUser = users.find((u) => u.bID === selectedId);
  const detailTitle = selectedId === 'new'
    ? 'New User'
    : (savedUser ? (savedUser.sDescription || savedUser.sName || 'User') : 'User');
  const inFinder = activeTab === 'access' && accessNav.mode === 'add';   // hide the tab bar in the finder

  // ---- list card: tap the row → Edit; quiet Disable/Enable (+ Delete for disabled) on the right.
  // No expand — the card already shows identity, access (sites · devices · cameras), and master (dot). ----
  const renderUserCard = (u) => {
    const disabled = isDisabled(u);
    const name = (u.sDescription || '').trim();
    const idText = name || u.sName;
    return (
      <div key={u.bID} className={'setup-user-card' + (selectedId === u.bID ? ' setup-user-card--active' : '')}>
        <div className="setup-user-card__head" role="button" onClick={() => selectUser(u)}>
          <div className="setup-user-card__text">
            {/* identity is the anchor: display name, else the email; a master gets a blue dot after it */}
            <div className="setup-user-card__id">
              <span className="setup-user-card__id-text">{idText}</span>
              {isMasterUser(u) ? <span className="setup-user-card__master" title="Dividia Master" /> : null}
            </div>
            <div className={'setup-user-card__sub' + (name ? '' : ' setup-user-card__sub--warn')}>
              {name ? u.sName : 'No display name'}
            </div>
            <div className="setup-user-card__access">{accessLine(u)}</div>
          </div>
          <div className="setup-user-card__actions">
            {u.bID !== callerId ? (
              <button type="button" className="setup-user-card__toggle"
                onClick={(e) => { e.stopPropagation(); doSetDisabled(u, disabled ? 0 : 1); }}>
                {disabled ? 'Enable' : 'Disable'}
              </button>
            ) : null}
            {disabled && u.bID !== callerId ? (
              <button type="button" className="setup-user-card__del" title="Delete permanently"
                aria-label={'Delete ' + idText}
                onClick={(e) => { e.stopPropagation(); doDelete(u); }}>
                <TrashIcon />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  // ---- detail: General ----
  const renderGeneral = () => (
    <>
      <div className="setup-section">Account</div>
      {/* Name + Display name pair on one row (2-col facts, like Camera General); the master toggle spans
          the full width below. (David 2026-08-24) */}
      <div className="setup-facts setup-facts--2col">
        <Row label="Name (email)">
          <input className="setup-input" value={form.email}
            onChange={(e) => patch({ email: e.target.value })} placeholder="name@company.com" />
        </Row>
        <Row label={form.id ? 'Display name' : 'Display name *'}>
          <input className="setup-input" value={form.description}
            onChange={(e) => patch({ description: e.target.value })} placeholder="e.g. Jane Smith" />
        </Row>
        {/* Master bit (bRole=1) — compact, in ACCOUNT, master-only (rights-acl §2). Full-width row. */}
        {isMaster ? (
          <div className="setup-form__row setup-form__row--wide">
            <label className="setup-checkbox-row">
              <input type="checkbox" checked={form.role === 1}
                onChange={(e) => patch({ role: e.target.checked ? 1 : 0 })} />
              <span>Dividia Master</span>
            </label>
            <div className="setup-hint">Full access to all customers and devices.</div>
          </div>
        ) : null}
      </div>
      {/* New user: choose the initial password inline. Existing user: Reset Password lives in the heading. */}
      {!form.id ? (
        <Row label="Password">
          <label className="setup-checkbox-row">
            <input type="checkbox" checked={form.generatePassword}
              onChange={(e) => patch({ generatePassword: e.target.checked, password: '' })} />
            <span>Auto-generate &amp; email</span>
          </label>
          {!form.generatePassword ? (
            <input className="setup-input setup-input--gap" type="password" value={form.password}
              onChange={(e) => patch({ password: e.target.value })} placeholder="Set a password" />
          ) : null}
        </Row>
      ) : null}
    </>
  );

  // ---- detail: Access (accordion device rows + finder) ----
  const renderGrant = (g) => {
    const open = expandedGrantId === g.sDeviceID;
    const cams = camsByDevice[g.sDeviceID] || [];
    const camNums = cams.map((c) => c.bCamera);
    const allOn = hasAllCameras(g.bCameras, camNums);
    const noCams = camNums.length ? countOf(g.bCameras, camNums) === 0 : cameraCount(g.bCameras) === 0;
    return (
      <div key={g.sDeviceID} className={'setup-grant' + (open ? ' setup-grant--open' : '')}>
        <div className="setup-grant__head" role="button" aria-expanded={open}
          onClick={() => setExpandedGrantId(open ? null : g.sDeviceID)}>
          <span className="setup-grant__caret">{open ? '▾' : '▸'}</span>
          <div className="setup-grant__main">
            {/* device name + customer·site share one line (space is at a premium; there's room here) */}
            <div className="setup-grant__title">
              <span className="setup-grant__name">{grantDevName(g.sDeviceID)}</span>
              {grantOrg(g.sDeviceID) ? <span className="setup-grant__org">{grantOrg(g.sDeviceID)}</span> : null}
            </div>
            {/* the rights/cameras summary is redundant once expanded (the editor shows it) — collapsed only */}
            {!open ? (
              <div className="setup-grant__meta">
                <span className="setup-grant__rights">{rightsSummary(g.bRights)}</span>
                <span className="setup-grant__sep">·</span>
                <span className={'setup-grant__cams' + (noCams ? ' setup-grant__cams--warn' : '')}>{camSummary(g)}</span>
              </div>
            ) : null}
          </div>
          <button type="button" className="setup-grant__remove" title="Remove this user's access to this device"
            aria-label={'Remove ' + grantDevName(g.sDeviceID)}
            onClick={(e) => { e.stopPropagation(); removeDevice(g.sDeviceID); }}>
            Remove access
          </button>
        </div>
        {open ? (
          <div className="setup-grant__editor">
            <div className="setup-subsection">Rights</div>
            <RightsChecklist value={g.bRights} onChange={(bits) => patchGrant(g.sDeviceID, { bRights: bits })} />
            <div className="setup-subsection">Cameras</div>
            {cams.length ? (
              <div className="setup-cam-grid">
                <label className="setup-cam-cell setup-cam-cell--all">
                  <input type="checkbox" checked={allOn}
                    onChange={() => patchGrant(g.sDeviceID, { bCameras: allOn ? '0' : maskForCameras(camNums) })} />
                  <span className="setup-cam-cell__label">All</span>
                </label>
                {cams.map((c) => {
                  // "<num> - <name>"; name falls back to "Cam <num>" when the device gives none
                  const label = c.bCamera + ' - ' + ((c.sName && c.sName.trim()) || ('Cam ' + c.bCamera));
                  return (
                    <label key={c.bCamera} className="setup-cam-cell" title={label}>
                      <input type="checkbox" checked={hasCamera(g.bCameras, c.bCamera)}
                        onChange={() => patchGrant(g.sDeviceID, { bCameras: toggleCamera(g.bCameras, c.bCamera) })} />
                      <span className="setup-cam-cell__label">{label}</span>
                    </label>
                  );
                })}
              </div>
            ) : <div className="setup-muted">This device reports no cameras.</div>}
          </div>
        ) : null}
      </div>
    );
  };

  const renderAccess = () => {
    if (accessNav.mode === 'add') {
      return (
        <div className="setup-subview">
          <button type="button" className="setup-subview__back" onClick={() => setAccessNav({ mode: 'list' })}>‹ Device Access</button>
          <div className="setup-subview__title">Add device</div>
          <div className="setup-hint setup-hint--spaced">Check a device to grant access. Set its rights and cameras afterward from the device list.</div>
          <AccessTree
            devices={devices}
            customers={customers}
            isChecked={(id) => form.grants.some((g) => g.sDeviceID === id)}
            onToggleDevice={toggleGrant}
          />
        </div>
      );
    }
    return (
      <div className="setup-access">
        {/* Device Access is a header row: the label on the left, the quick-set link + "+ Add device"
            grouped on the right — so the quick-set no longer eats a full row of vertical space. Its
            body (rights + all-cameras + Apply) drops in below the header only when opened. */}
        <div className="setup-access__header">
          <div className="setup-section">Device Access ({form.grants.length})</div>
          <div className="setup-access__actions">
            {form.grants.length >= 2 ? (
              <button type="button" className="setup-quickset__link" aria-expanded={showQuickSet}
                onClick={() => setShowQuickSet((s) => !s)}>
                <span className="setup-quickset__chev">{showQuickSet ? '▼' : '▶'}</span> Apply to all…
              </button>
            ) : null}
            <button type="button" className="setup-access__add" onClick={() => setAccessNav({ mode: 'add' })}>+ Add device</button>
          </div>
        </div>
        {form.grants.length >= 2 && showQuickSet ? (
          <div className="setup-quickset__body">
            {/* a titled panel with an explicit × close — the trigger link is up in the header row, so
                the panel needs to read as an open, closable thing on its own */}
            <div className="setup-quickset__bar">
              <span className="setup-quickset__heading">Apply rights to all devices</span>
              <button type="button" className="setup-quickset__close" aria-label="Close" onClick={() => setShowQuickSet(false)}>×</button>
            </div>
            <RightsChecklist value={quickRights} onChange={setQuickRights} />
            <div className="setup-quickset__foot">
              <label className="setup-checkbox-row setup-quickset__allcams">
                <input type="checkbox" checked={quickAllCams} onChange={(e) => setQuickAllCams(e.target.checked)} />
                <span>Also grant all cameras on each device</span>
              </label>
              <button type="button" className="setup-btn--secondary" onClick={applyQuickSet}>
                Apply to all {form.grants.length} devices
              </button>
            </div>
          </div>
        ) : null}
        {form.grants.length === 0 ? (
          <div className="setup-muted">No device access yet. Add a device to grant this user access.</div>
        ) : (
          <div className="setup-grants">
            {form.grants.map(renderGrant)}
          </div>
        )}
      </div>
    );
  };

  return (
    <SetupDrill
      detailOpen={detailOpen}
      backLabel="Users"
      headingNav={!inFinder ? (
        <div className="setup-tabbar">
          <button type="button"
            className={'setup-tabbar__tab' + (activeTab === 'general' ? ' setup-tabbar__tab--active' : '')}
            onClick={() => { setActiveTab('general'); setAccessNav({ mode: 'list' }); }}>General</button>
          <button type="button"
            className={'setup-tabbar__tab' + (activeTab === 'access' ? ' setup-tabbar__tab--active' : '')}
            onClick={() => setActiveTab('access')}>Access</button>
        </div>
      ) : null}
      title={detailTitle}
      onBack={closeDetail}
      wideList
      fillBody={detailOpen && activeTab === 'access' && accessNav.mode === 'list'}
      headingAction={detailOpen && form.id ? (
        <button className="setup-linkbtn" disabled={busy} onClick={resetPw}>Reset password</button>
      ) : null}
      list={(
        <SetupListPanel
          searchFirst
          newLabel="+ New"
          onNew={newUser}
          filter={filter}
          setFilter={setFilter}
          filterPlaceholder="Filter by name, email, or location…"
          loading={loading}
          empty={!filtered.length}
          emptyText={listTab === 'active' ? 'No active users.' : 'No disabled users.'}
          subhead={(
            <div className="setup-segmented" role="tablist">
              <button type="button" role="tab" aria-selected={listTab === 'active'}
                className={'setup-segmented__opt' + (listTab === 'active' ? ' setup-segmented__opt--active' : '')}
                onClick={() => setListTab('active')}>Active ({activeCount})</button>
              <button type="button" role="tab" aria-selected={listTab === 'disabled'}
                className={'setup-segmented__opt' + (listTab === 'disabled' ? ' setup-segmented__opt--active' : '')}
                onClick={() => setListTab('disabled')}>Disabled ({disabledCount})</button>
            </div>
          )}
        >
          {filtered.map(renderUserCard)}
        </SetupListPanel>
      )}
      actions={detailOpen ? (
        <>
          <div className="setup-actions__spacer" />
          {dirty ? (
            <button className="setup-btn--secondary" disabled={busy} onClick={revert}>Revert</button>
          ) : null}
          <button
            className={'setup-btn--save' + (canSave ? '' : ' setup-btn--save-clean')}
            disabled={busy || !canSave} onClick={save}>
            {busy ? 'Saving…' : (form.id ? 'Save' : 'Create User')}
          </button>
        </>
      ) : null}
    >
      {detailOpen ? (
        <>
          <div className="setup-tabbar__body">
            {/* CloudAPI limits a non-master to users and device grants inside their ADMIN scope. Keep the
                master-bit toggle hidden below, but allow the scoped create and edit flow to submit. */}
            <fieldset className="setup-fieldset">
              {activeTab === 'general' ? renderGeneral() : renderAccess()}
            </fieldset>
          </div>

          {needsGrant ? (
            <div className="setup-hint setup-hint--spaced">Grant access to at least one device (Access tab) before creating this user.</div>
          ) : null}
          {error ? <div className="setup-error">{error}</div> : null}
          {notice ? <div className="setup-notice">{notice}</div> : null}
        </>
      ) : null}
    </SetupDrill>
  );
};

const mapStateToProps = (state) => ({
  sSess: state.auth.sSess,
  bRole: state.user.bRole,
  callerId: state.user.bID,   // self-id — the logged-in user can't disable/delete their own account
});

export default connect(mapStateToProps)(CloudUserSetup);
